import { IContextMenuItem } from '@smartshell/ui-kit';

import { openConfirmDialog } from 'services/Confirmation';
import notification from 'services/notificationService';

import s from '../ContextMenuItems.module.scss';
import { CtxMenuHost } from '../interfaces';
import { TGetManageHostItem } from './interfaces';

export const getManageHostItem: TGetManageHostItem = ({
  t,
  permissions,
  selectedHostsIds,
  setServiceMode,
  isRemoteControlAvailable,
  remoteControlCCBoot,
  remoteControlPassword,
  isCommentsAvailable,
  isRunFileAvailable,
  openRunFileModal,
  openHostCommentsModal,
  openManageHostModal,
  deleteHost,
  unselectHosts,
  updateOnlineBookingEnabled,
  hosts,
  openGeneratePinModal,
}) => {
  const manageHostItems: Array<IContextMenuItem<CtxMenuHost>> = [];
  const selectedHosts = hosts?.filter((host) =>
    selectedHostsIds?.includes(host.id)
  );

  if (permissions?.has('host_command_manage')) {
    manageHostItems.push({
      key: 'service',
      text: ({ in_service }) =>
        t(in_service ? 'host.ctx.service.off' : 'host.ctx.service.on'),
      iconName: 'ConeIcon',
      onClick: async ({ id, in_service }) => {
        if (!selectedHostsIds?.length) {
          setServiceMode({
            variables: {
              id,
              flag: { value: !in_service },
            },
          }).catch(console.error);

          return;
        }

        for (const id of selectedHostsIds) {
          await setServiceMode({
            variables: {
              id: id,
              flag: { value: !in_service },
            },
          }).catch(console.error);
        }
      },
      isLastInGroup: true,
      disabled: ({ client_sessions: [activeSession], locked, in_service }) =>
        Boolean(activeSession || (selectedHostsIds?.length ?? 0) > 1) ||
        (locked && !in_service),
    });

    if (isRemoteControlAvailable) {
      manageHostItems.push(
        {
          key: 'connect',
          text: t('host.ctx.connect'),
          iconName: 'CursorIcon',
          disabled: (host: CtxMenuHost) =>
            !host.online ||
            host.locked ||
            !host.mac_addr ||
            !!(selectedHostsIds && selectedHostsIds.length > 1),
          onClick: ({ ip_addr, mac_addr }: CtxMenuHost) => {
            if (selectedHostsIds?.length === 1) {
              return open(
                `sstvn://${ip_addr ?? mac_addr?.replaceAll(':', '-')}&${
                  remoteControlCCBoot ? 'ccboot_mode' : remoteControlPassword
                }`
              );
            }

            notification.info({
              t,
              type: 'warning',
              title: t('host.ctx.tooMany.title'),
              message: t('host.ctx.tooMany.message'),
            });
          },
        },
        {
          key: 'screenView',
          text: t('host.ctx.screenView'),
          iconName: 'MonitorArrowInIcon',
          disabled: (host: CtxMenuHost) =>
            !host.online ||
            host.locked ||
            !host.mac_addr ||
            !!(selectedHostsIds && selectedHostsIds.length > 1),
          onClick: ({ mac_addr, ip_addr }: CtxMenuHost) => {
            if (selectedHostsIds?.length === 1) {
              return open(
                `sstvn://${ip_addr ?? mac_addr?.replaceAll(':', '-')}&${
                  remoteControlCCBoot ? 'ccboot_mode' : remoteControlPassword
                }&viewonly`
              );
            }

            notification.info({
              t,
              type: 'warning',
              title: t('host.ctx.tooMany.title'),
              message: t('host.ctx.tooMany.message'),
            });
          },
        }
      );
    }

    manageHostItems.push({
      key: 'run',
      text: t('host.ctx.execute'),
      iconName: 'TerminalIcon',
      disabled: (host: CtxMenuHost) =>
        !isRunFileAvailable || !host.online || host.locked,
      onClick: openRunFileModal,
    });
  }
  if (permissions?.has('comment_read')) {
    manageHostItems.push({
      key: 'note',
      text: t('host.ctx.note'),
      iconName: 'NoteIcon',
      onClick: openHostCommentsModal,
      disabled: ({ locked }) =>
        !isCommentsAvailable ||
        Boolean((selectedHostsIds?.length ?? 0) > 1) ||
        locked,
    });
  }
  if (permissions?.has('host_manage'))
    manageHostItems.push(
      {
        key: 'pin',
        text: t('host.ctx.generate_pin'),
        iconName: 'HashtagPinIcon',
        disabled: (selectedHostsIds?.length || 0) > 1,
        onClick: openGeneratePinModal,
      },
      {
        key: 'online-booking',
        text: ({ online_booking_enabled }) =>
          online_booking_enabled
            ? t('host.ctx.online_booking.not_enabled')
            : t('host.ctx.online_booking.enabled'),
        iconName: ({ online_booking_enabled }) =>
          online_booking_enabled ? 'SmartGamerDashedIcon' : 'SmartGamerIcon',
        onClick: ({ id, online_booking_enabled }) =>
          updateOnlineBookingEnabled({
            id,
            online_booking_enabled: !online_booking_enabled,
          }),
        className: s.online_booking,
        disabled: (selectedHostsIds?.length ?? 0) > 1,
      },
      {
        key: 'host-edit',
        text: t('edit'),
        iconName: 'EditIcon',
        onClick: openManageHostModal,
        disabled: ({ client_sessions: [activeSession] }) =>
          Boolean(activeSession || (selectedHostsIds?.length ?? 0) > 1),
      },
      {
        key: 'host-delete',
        text: t('delete'),
        iconName: 'BucketIcon',
        className: s['red-text'],
        disabled: ({ in_service, client_sessions: [activeSession] }) => {
          if (selectedHostsIds?.length === 1)
            return in_service || Boolean(activeSession);

          if (
            selectedHostsIds &&
            selectedHostsIds?.length > 1 &&
            selectedHosts
          ) {
            if (
              selectedHosts.some(
                (host) => host.in_service || host.client_sessions.length
              )
            ) {
              return true;
            }
          }

          return false;
        },
        onClick: async ({ id, alias }) => {
          openConfirmDialog(t, 'remove', {
            message: t('host.delete.confirm.message', alias),
          }).then(async ({ status }) => {
            if (status === 'DECLINED') {
              return;
            }

            if (!selectedHostsIds?.length) {
              deleteHost({
                variables: { id },
              })
                .then(unselectHosts)
                .catch(console.error);

              return;
            }

            for (const id of selectedHostsIds) {
              await deleteHost({
                variables: { id },
              })
                .then(unselectHosts)
                .catch(console.error);
            }
          });
        },
      }
    );

  return !manageHostItems.length
    ? null
    : {
        key: 'control',
        text: t('host.ctx.control'),
        iconName: 'GearIcon',
        subMenu: manageHostItems,
      };
};
